import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CaretRight } from 'phosphor-react';
import { SBack } from '@/components/ui/molecules/SBack';
import { ThemeToggle } from '@/components/ui/atoms/ThemeToggle';
import { useMembership } from '@/hooks/useMembership';
import { useMemberEntitlements } from '@/hooks/useMemberEntitlements';
import { facilityTypeTitles } from '@/types/booking.types';
import './Membership.css';

function fmtDate(s: string): string {
  try {
    const d = new Date(s);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return '—'; }
}

function fmtPrice(n: number): string {
  return `$${n.toFixed(2)}`;
}

function billingLabel(cycle?: string): string {
  return ({ MONTHLY: '/mo', ANNUAL: '/yr', QUARTERLY: '/qtr' } as Record<string, string>)[cycle || ''] || '';
}

const Membership: React.FC = () => {
  const navigate = useNavigate();
  const { data: membership, isLoading, error } = useMembership();
  const { data: entitlementsResponse } = useMemberEntitlements();
  const entitlements = entitlementsResponse?.data;

  return (
    <div className="mem">
      <div className="mem__header">
        <SBack onClick={() => navigate(-1)} />
        <span className="mem__title">Membership</span>
        <ThemeToggle />
      </div>

      {isLoading ? (
        <div className="mem__loading">Loading…</div>
      ) : error || !membership ? (
        <div className="mem__loading">Failed to load membership</div>
      ) : (
        <div className="mem__body">

          {/* Plan details */}
          <div>
            <p className="mem__section-label">Plan</p>
            <div className="mem__card">
              <div className="mem__row">
                <div className="mem__field">
                  <span className="mem__field-label">Plan Name</span>
                  <span className="mem__field-value">{membership.plan?.name || '—'}</span>
                </div>
                <span className={`mem__badge mem__badge--${membership.status?.toLowerCase() === 'active' ? 'green' : 'grey'}`}>
                  {membership.status}
                </span>
              </div>
              <div className="mem__divider" />
              <div className="mem__cols">
                <div className="mem__field">
                  <span className="mem__field-label">Auto Renew</span>
                  <span className="mem__field-value">Yes</span>
                </div>
                <div className="mem__field">
                  <span className="mem__field-label">Pricing</span>
                  <span className="mem__field-value">
                    {fmtPrice(membership.price)}{billingLabel(membership.plan?.billingCycle)}
                  </span>
                </div>
              </div>
              <div className="mem__divider" />
              <div className="mem__cols">
                <div className="mem__field">
                  <span className="mem__field-label">Start Date</span>
                  <span className="mem__field-value">{fmtDate(membership.startDate)}</span>
                </div>
                <div className="mem__field">
                  <span className="mem__field-label">End Date</span>
                  <span className="mem__field-value">{fmtDate(membership.endDate)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Entitlements */}
          {entitlements && entitlements.length > 0 && (
            <div>
              <p className="mem__section-label">Entitlements</p>
              <div className="mem__card">
                {entitlements.map((e, i) => (
                  <React.Fragment key={e.serviceType}>
                    {i > 0 && <div className="mem__divider" />}
                    <div
                      className={`mem__field${e.active === false ? ' mem__field--disabled' : ''}`}
                      title={e.active === false ? 'Facility no longer available.' : undefined}
                    >
                      <span className="mem__field-label">{facilityTypeTitles[e.facilityType]}</span>
                      <span className="mem__field-value">{`${e.remainingCount} remaining`}</span>
                      {e.active === false && (
                        <span className="mem__field-hint">Facility no longer available.</span>
                      )}
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}

          {/* Billing */}
          <div>
            <p className="mem__section-label">Billing</p>
            <div className="mem__card">
              <div className="mem__billing-row">
                <div className="mem__field">
                  <span className="mem__field-label">Next Billing Date</span>
                  <span className="mem__field-value">{fmtDate(membership.endDate)}</span>
                </div>
                <span className="mem__billing-amount">{fmtPrice(membership.price)}</span>
              </div>
            </div>
          </div>

          {/* Agreement */}
          <div>
            <div className="mem__card">
              <button
                className="mem__nav-row"
                onClick={() => console.log('Navigate to membership agreement')}
              >
                <span className="mem__nav-label">Tee Box Membership Agreement</span>
                <CaretRight size={16} color="#9e9a90" />
              </button>
            </div>
          </div>

          {/* Cancel */}
          {/* <button className="mem__cancel" onClick={() => console.log('Cancel subscription')}>
            Cancel Subscription
          </button> */}

        </div>
      )}
    </div>
  );
};

export default Membership;
